import * as Types from '@frameio/graphql-client/generated/types';

import { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import gql from 'graphql-tag';

import { useQuery, useLazyQuery, useMutation, useFragment, useSubscription } from '@apollo/client';
import type { QueryHookOptions, LazyQueryHookOptions, MutationHookOptions, SubscriptionHookOptions, UseFragmentOptions } from '@apollo/client';

export type WatermarkTemplateCompleteFragment = { __typename?: 'WatermarkTemplate', id: string, deletedAt?: string | null, inUse: boolean, name: string, blocks?: Array<{ __typename?: 'WatermarkBlock', alpha?: number | null, fontSize?: Types.WatermarkFontSize | null, name?: string | null, position?: Types.WatermarkPosition | null, positionReferencePoint?: Types.WatermarkPosition | null, positionX?: number | null, positionY?: number | null, rotation?: number | null, scrollText?: Types.WatermarkScrollText | null, textAlignment?: Types.WatermarkTextAlignment | null, textColor?: string | null, textShadow?: boolean | null, dataPoints?: Array<{ __typename?: 'WatermarkDataPointCustomText', value?: string | null } | { __typename: 'WatermarkDataPointEmail' } | { __typename: 'WatermarkDataPointIpAddress' } | { __typename: 'WatermarkDataPointTimestamp' } | { __typename: 'WatermarkDataPointUserInput' } | { __typename: 'WatermarkDataPointUserName' } | null> | null } | null> | null };

export type WatermarkTemplateBasicFragment = { __typename?: 'WatermarkTemplate', id: string, name: string };

export type AccountWatermarkTemplatesFragment = { __typename?: 'Account', watermarkTemplates?: { __typename?: 'WatermarkTemplateConnection', totalCount: number, nodes: Array<{ __typename?: 'WatermarkTemplate', id: string, deletedAt?: string | null, inUse: boolean, name: string, blocks?: Array<{ __typename?: 'WatermarkBlock', alpha?: number | null, fontSize?: Types.WatermarkFontSize | null, name?: string | null, position?: Types.WatermarkPosition | null, positionReferencePoint?: Types.WatermarkPosition | null, positionX?: number | null, positionY?: number | null, rotation?: number | null, scrollText?: Types.WatermarkScrollText | null, textAlignment?: Types.WatermarkTextAlignment | null, textColor?: string | null, textShadow?: boolean | null, dataPoints?: Array<{ __typename?: 'WatermarkDataPointCustomText', value?: string | null } | { __typename: 'WatermarkDataPointEmail' } | { __typename: 'WatermarkDataPointIpAddress' } | { __typename: 'WatermarkDataPointTimestamp' } | { __typename: 'WatermarkDataPointUserInput' } | { __typename: 'WatermarkDataPointUserName' } | null> | null } | null> | null }> } | null };

export type ProjectWatermarkTemplatesFragment = { __typename?: 'Project', watermarkTemplates?: { __typename?: 'WatermarkTemplateConnection', totalCount: number, nodes: Array<{ __typename?: 'WatermarkTemplate', id: string, deletedAt?: string | null, inUse: boolean, name: string, blocks?: Array<{ __typename?: 'WatermarkBlock', alpha?: number | null, fontSize?: Types.WatermarkFontSize | null, name?: string | null, position?: Types.WatermarkPosition | null, positionReferencePoint?: Types.WatermarkPosition | null, positionX?: number | null, positionY?: number | null, rotation?: number | null, scrollText?: Types.WatermarkScrollText | null, textAlignment?: Types.WatermarkTextAlignment | null, textColor?: string | null, textShadow?: boolean | null, dataPoints?: Array<{ __typename?: 'WatermarkDataPointCustomText', value?: string | null } | { __typename: 'WatermarkDataPointEmail' } | { __typename: 'WatermarkDataPointIpAddress' } | { __typename: 'WatermarkDataPointTimestamp' } | { __typename: 'WatermarkDataPointUserInput' } | { __typename: 'WatermarkDataPointUserName' } | null> | null } | null> | null }> } | null };

export type WorkspaceWatermarkTemplatesFragment = { __typename?: 'Workspace', watermarkTemplates?: { __typename?: 'WatermarkTemplateConnection', totalCount: number, nodes: Array<{ __typename?: 'WatermarkTemplate', id: string, deletedAt?: string | null, inUse: boolean, name: string, blocks?: Array<{ __typename?: 'WatermarkBlock', alpha?: number | null, fontSize?: Types.WatermarkFontSize | null, name?: string | null, position?: Types.WatermarkPosition | null, positionReferencePoint?: Types.WatermarkPosition | null, positionX?: number | null, positionY?: number | null, rotation?: number | null, scrollText?: Types.WatermarkScrollText | null, textAlignment?: Types.WatermarkTextAlignment | null, textColor?: string | null, textShadow?: boolean | null, dataPoints?: Array<{ __typename?: 'WatermarkDataPointCustomText', value?: string | null } | { __typename: 'WatermarkDataPointEmail' } | { __typename: 'WatermarkDataPointIpAddress' } | { __typename: 'WatermarkDataPointTimestamp' } | { __typename: 'WatermarkDataPointUserInput' } | { __typename: 'WatermarkDataPointUserName' } | null> | null } | null> | null }> } | null };

export type HasSessionBasedTemplatesFragment = { __typename?: 'Account', id: string, hasSessionBasedTemplates?: boolean | null };

export const WatermarkTemplateBasic = /*#__PURE__*/ gql`
    fragment WatermarkTemplateBasic on WatermarkTemplate {
  id
  name
}
    ` as unknown as DocumentNode<WatermarkTemplateBasicFragment, unknown>;
export const WatermarkTemplateComplete = /*#__PURE__*/ gql`
    fragment WatermarkTemplateComplete on WatermarkTemplate {
  id
  blocks {
    alpha
    dataPoints {
      ... on WatermarkDataPointEmail {
        __typename
      }
      ... on WatermarkDataPointUserName {
        __typename
      }
      ... on WatermarkDataPointIpAddress {
        __typename
      }
      ... on WatermarkDataPointTimestamp {
        __typename
      }
      ... on WatermarkDataPointCustomText {
        value
      }
      ... on WatermarkDataPointUserInput {
        __typename
      }
    }
    fontSize
    name
    position
    positionReferencePoint
    positionX
    positionY
    rotation
    scrollText
    textAlignment
    textColor
    textShadow
  }
  deletedAt
  inUse
  name
}
    ` as unknown as DocumentNode<WatermarkTemplateCompleteFragment, unknown>;
export const AccountWatermarkTemplates = /*#__PURE__*/ gql`
    fragment AccountWatermarkTemplates on Account {
  watermarkTemplates(page: {first: 10000}) {
    nodes {
      ...WatermarkTemplateComplete
    }
    totalCount
  }
}
    ${WatermarkTemplateComplete}` as unknown as DocumentNode<AccountWatermarkTemplatesFragment, unknown>;
export const ProjectWatermarkTemplates = /*#__PURE__*/ gql`
    fragment ProjectWatermarkTemplates on Project {
  watermarkTemplates(page: {first: 10000}) {
    nodes {
      ...WatermarkTemplateComplete
    }
    totalCount
  }
}
    ${WatermarkTemplateComplete}` as unknown as DocumentNode<ProjectWatermarkTemplatesFragment, unknown>;
export const WorkspaceWatermarkTemplates = /*#__PURE__*/ gql`
    fragment WorkspaceWatermarkTemplates on Workspace {
  watermarkTemplates(page: {first: 10000}) {
    nodes {
      ...WatermarkTemplateComplete
    }
    totalCount
  }
}
    ${WatermarkTemplateComplete}` as unknown as DocumentNode<WorkspaceWatermarkTemplatesFragment, unknown>;
export const HasSessionBasedTemplates = /*#__PURE__*/ gql`
    fragment HasSessionBasedTemplates on Account {
  id
  hasSessionBasedTemplates
}
    ` as unknown as DocumentNode<HasSessionBasedTemplatesFragment, unknown>;

        

        
export const useWatermarkTemplateCompleteFragment = (
  input: string | Omit<UseFragmentOptions<WatermarkTemplateCompleteFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'WatermarkTemplate' } };
    return input;
  })();

  return useFragment<WatermarkTemplateCompleteFragment>({
    fragment: WatermarkTemplateComplete,
    fragmentName: 'WatermarkTemplateComplete',
    ...options,
  });
};
  
        
        

        
export const useWatermarkTemplateBasicFragment = (
  input: string | Omit<UseFragmentOptions<WatermarkTemplateBasicFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'WatermarkTemplate' } };
    return input;
  })();

  return useFragment<WatermarkTemplateBasicFragment>({
    fragment: WatermarkTemplateBasic,
    fragmentName: 'WatermarkTemplateBasic',
    ...options,
  });
};
  
        
        

        
export const useAccountWatermarkTemplatesFragment = (
  input: string | Omit<UseFragmentOptions<AccountWatermarkTemplatesFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<AccountWatermarkTemplatesFragment>({
    fragment: AccountWatermarkTemplates,
    fragmentName: 'AccountWatermarkTemplates',
    ...options,
  });
};
  
        
        

        
export const useProjectWatermarkTemplatesFragment = (
  input: string | Omit<UseFragmentOptions<ProjectWatermarkTemplatesFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Project' } };
    return input;
  })();

  return useFragment<ProjectWatermarkTemplatesFragment>({
    fragment: ProjectWatermarkTemplates,
    fragmentName: 'ProjectWatermarkTemplates',
    ...options,
  });
};
  
        
        

        
export const useWorkspaceWatermarkTemplatesFragment = (
  input: string | Omit<UseFragmentOptions<WorkspaceWatermarkTemplatesFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Workspace' } };
    return input;
  })();

  return useFragment<WorkspaceWatermarkTemplatesFragment>({
    fragment: WorkspaceWatermarkTemplates,
    fragmentName: 'WorkspaceWatermarkTemplates',
    ...options,
  });
};
  
        
        

        
export const useHasSessionBasedTemplatesFragment = (
  input: string | Omit<UseFragmentOptions<HasSessionBasedTemplatesFragment, any>, 'fragment' | 'fragmentName'> | undefined | null
) => {
  const options = (() => {
    if (!input) return { from: null };
    if (typeof input === 'string') return { from: { id: input, __typename: 'Account' } };
    return input;
  })();

  return useFragment<HasSessionBasedTemplatesFragment>({
    fragment: HasSessionBasedTemplates,
    fragmentName: 'HasSessionBasedTemplates',
    ...options,
  });
};
  
        